import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { GrossMarginReportSliceState, setIsLoading, setQuery, setSearchFields } from "slices/finance/gross-margin-report/reducer"
import { getGrossMarginReport, getMostRecentSyncLog, syncGrossMarginData } from "slices/thunk"
import { downloadGrossMarginReportCSV, getGrossMarginReportSearchFields } from "slices/finance/gross-margin-report/thunk"
import GrossMarginReportTemplate from "Components/templates/gross-margin-report"
import SearchGrossMarginReportForm from "Components/organisms/forms/search-gross-margin-report"
import { SelectOption } from "Components/molecule/inputs/select-input"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { IGrossMarginReportQuery } from "models/gross-margin-report"
import { SyncLogSliceState } from "slices/sync-log/reducer"
import { SyncLogTypes } from "models/sync-log"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import ReportButtons from "Components/molecule/report-buttons"
import GrossMarginValueModal from "Components/organisms/gross-margin-value-modal"
import { GrossMarginValueClick } from "Components/templates/gross-margin-report"
import useManual from "hooks/useManual"
import useSyncProcess from "hooks/useSyncProcess"
import SyncButton from "Components/molecule/sync-button"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import { toDepartmentOptions } from "utils/department-options"

const VANCOUVER_BRANCH_CODE = "VANCOUVER"
const CALGARY_BRANCH_CODE = "CALGARY"
const CORPORATE_OFFICE_BRANCH_CODE = "CORPORATE"
const DEFAULT_BRANCH_CODES = [VANCOUVER_BRANCH_CODE, CALGARY_BRANCH_CODE, CORPORATE_OFFICE_BRANCH_CODE]

const selectGrossMarginReport = createSelector(
    (state: any) => state.GrossMarginReport,
    ({ query, report, isLoading, branches, departments }: GrossMarginReportSliceState) => ({
        query, report, isLoading, branches, departments
    })
)

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({
        syncLogs
    })
)

const GrossMarginReportPage = () => {
    document.title = "Gross Margin | IbexIQ"
    const { handleOpenManual } = useManual("finance/gross-margin-report");

    const dispatch = useDispatch() as any
    const [selectedBranches, setSelectedBranches] = useState<SelectOption[]>([])
    const [selectedDepartments, setSelectedDepartments] = useState<SelectOption[]>([])
    const [showSyncModal, setShowSyncModal] = useState<boolean>(false)
    const [valueClick, setValueClick] = useState<GrossMarginValueClick | null>(null)
    const [isExporting, setIsExporting] = useState(false)
    const { query, report, isLoading, branches, departments } = useSelector(selectGrossMarginReport)
    const {
        syncLogs
    } = useSelector(selectSyncLog)


    const handleOpenSyncModal = () => setShowSyncModal(true)
    const handleCloseSyncModal = () => setShowSyncModal(false)

    const fiscal = new FiscalCalendar(DEFAULT_FISCAL_START);

    // Generate all options from FY2019 P1 → next fiscal year current period
    const fiscalPeriodOptions = useMemo(() => {
        const options: { value: string; label: string }[] = [];

        const today = new Date();
        const { fiscalYear: currentFY, period: currentPeriod } = fiscal.dateToFiscal(today);

        const startFY = 2019;
        const startPeriod = 1;
        const endFY = currentFY + 1;
        const endPeriod = currentPeriod;

        for (let fy = startFY; fy <= endFY; fy++) {
            const maxPeriod = fy === endFY ? endPeriod : 12;
            const minPeriod = 1;

            for (let p = minPeriod; p <= maxPeriod; p++) {
                options.push({
                    value: fy.toString() + String(p).padStart(2, "0"),        // e.g. "2024-03"
                    label: fiscal.fiscalToLabel(fy, p),    // e.g. "October 2023"
                });
            }
        }

        return options;
    }, []);

    const initialize = async (query: IGrossMarginReportQuery) => {
        let currentBranches = branches

        if (!branches.length || !departments.length) {
            const data = await getGrossMarginReportSearchFields()
            if (data) {
                dispatch(setSearchFields(data))
                currentBranches = data.branches
            }
        }

        let q = query
        if (!q.branchIds?.length) {
            const defaultBranches = currentBranches.filter(b => DEFAULT_BRANCH_CODES.includes(b.code.trim()))
            if (defaultBranches.length) {
                q = { ...q, branchIds: defaultBranches.map(b => b.id) }
                setSelectedBranches(defaultBranches.map(b => ({ label: b.name, value: b.id })))
                dispatch(setQuery(q))
            }
        }

        const promises: any[] = [dispatch(getGrossMarginReport(q))]

        if (!syncLogs[SyncLogTypes.PROJECTS])
            promises.push(dispatch(getMostRecentSyncLog(SyncLogTypes.GROSS_MARGIN_REPORT)))

        await Promise.all(promises);
        dispatch(setIsLoading(false))
    }

    useEffect(() => {
        initialize(query)
    }, [])

    const handleClearSearch = () => {
        setSelectedBranches([])
        setSelectedDepartments([]);

        // Reports look back one month: fiscal year of last month, period 1 to last month
        const { fiscalYear, period } = FiscalCalendar.lastMonth()
        const fromFiscalPeriod = fiscalYear * 100 + 1
        const toFiscalPeriod = fiscalYear * 100 + period

        dispatch(setQuery({
            fromFiscalPeriod,
            toFiscalPeriod,
            departmentIds: [],
            branchIds: []
        }));

    }

    const handleSearch = useCallback(async () => {
        const departmentIds = selectedDepartments.map(s => s.value);
        const branchIds = selectedBranches.map(b => b.value);

        const q = { ...query };
        q.branchIds = branchIds;
        q.departmentIds = departmentIds;

        dispatch(setIsLoading(true))
        await dispatch(getGrossMarginReport(q))
        dispatch(setIsLoading(false))
    }, [query, setIsLoading, selectedBranches, selectedDepartments])


    const handleExportData = () => {
        setIsExporting(true)
        try {
            downloadGrossMarginReportCSV(report, query)
        } finally {
            setIsExporting(false)
        }
    }

    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.GROSS_MARGIN_REPORT,
        start: syncGrossMarginData,
        onFinished: handleSearch,
    })

    const handleSyncProjects = async () => {
        handleCloseSyncModal();
        await startSync()
    }

    return (
        <GrossMarginReportTemplate
            title="Finance"
            breadcrumbItem="Gross Margin"
            actions={
                <>
                    <SyncButton
                        id="projectsSyncBtn"
                        isSyncing={isSyncingData}
                        syncLog={syncLogs[SyncLogTypes.GROSS_MARGIN_REPORT]}
                        onClick={handleOpenSyncModal}
                    />
                    <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
                </>
            }
            isLoading={isLoading}
            tableActions={<ReportButtons title="Gross Margin" module="Finance" fullExport={handleExportData} />}
            report={report}
            onValueClick={setValueClick}
            filters={
                <SearchGrossMarginReportForm
                    branches={branches.map(b => ({ label: b.name, value: b.id }))}
                    departments={toDepartmentOptions(departments)}
                    fiscalPeriodOptions={fiscalPeriodOptions}
                    selectedBranches={selectedBranches}
                    selectedDepartments={selectedDepartments}
                    fromFiscalPeriod={query.fromFiscalPeriod ?? parseFloat((fiscal.getFiscalYear(new Date()).toString() + fiscal.getFiscalPeriod(new Date()).toString()))}
                    toFiscalPeriod={query.toFiscalPeriod ?? parseFloat((fiscal.getFiscalYear(new Date()).toString() + fiscal.getFiscalPeriod(new Date()).toString()))}
                    isSearching={false}
                    handleSearch={handleSearch}
                    handleClearSearch={handleClearSearch}
                    onChangeBranches={setSelectedBranches}
                    onChangeDepartments={setSelectedDepartments}
                    onChangeTo={function (toFiscalPeriod?: number): void {
                        dispatch(setQuery({ ...query, toFiscalPeriod }))
                    }}
                    onChangeFrom={function (fromFiscalPeriod?: number): void {
                        dispatch(setQuery({ ...query, fromFiscalPeriod }))
                    }} />
            }

            modal={<>
                <GrossMarginValueModal
                    click={valueClick}
                    onClose={() => setValueClick(null)}
                    query={{ ...query, branchIds: selectedBranches.map(b => b.value), departmentIds: selectedDepartments.map(d => d.value) }}
                />
                <AreYouSureModal
                isOpen={showSyncModal}
                onModalClose={handleCloseSyncModal}
                handleAreYouSureDecline={async () => handleCloseSyncModal()}
                handleAreYouSureAccept={handleSyncProjects}
                modalTitle="Sync Gross Margin"
                subTitle={"Sync gross margin report"}
                areYouSureacceptText="Sync"
                areYouSuredeclineText="Cancel"
            />
            </>}
        />
    )
}

export default GrossMarginReportPage